import type { UseDonutChartOptionsProps } from "@/models/Chart";
import { useMemo } from "react";
import { filterSubOnePercentSlices } from "@/utils/donut-chart-utils";

export const useDonutChartOptions = ({
  config,
  chartData,
  emphasisData: providedEmphasisData,
  isConfigPreview = false,
}: UseDonutChartOptionsProps) => {
  // Helper function to format percentage — values under 0.5% show as the
  // configured Sub-1% Style ("<1%" or "0%") rather than rounding down to "0%".
  const formatPercentage = (value: number): string => {
    if (config.percentageFormatting === 0 && value > 0 && value < 0.5) {
      return config.subOnePercentStyle || "<1%";
    }
    return value.toFixed(
      config.percentageFormatting !== undefined
        ? config.percentageFormatting
        : 1,
    );
  };

  // Resolve a slice's display label from its stable source name. Rollups use
  // their own name directly (no separate override); regular slices can carry
  // a Slice Customization override. Applied only at display time so every
  // other lookup (grouping, manualColors, emphasis, N) keeps using the
  // stable source name.
  const resolveLabel = (name: string): string =>
    config.sliceCustomization?.labelOverrides[name] ?? name;

  // Tooltip, data-label, and center-label text are all rendered with
  // useHTML: true. Slice Customization labels, the emphasis custom text, and
  // the center text are all user-entered, so they must be escaped before
  // being interpolated into any of those HTML strings (including inside
  // HTML attributes like title="") to prevent script/markup injection.
  const escapeHtml = (value: string): string =>
    value.replace(/[&<>"']/g, (char) => {
      switch (char) {
        case "&":
          return "&amp;";
        case "<":
          return "&lt;";
        case ">":
          return "&gt;";
        case '"':
          return "&quot;";
        case "'":
          return "&#39;";
        default:
          return char;
      }
    });

  // Helper function to get arrow indicator based on significantDifference
  const getSignificanceArrow = (significantDifference?: -1 | 0 | 1): string => {
    if (significantDifference === 1) {
      return ' <span style="color: #10B981;">▲</span>'; // Green up arrow
    } else if (significantDifference === -1) {
      return ' <span style="color: #EF4444;">▼</span>'; // Red down arrow
    }
    return ""; // No arrow for 0 or undefined
  };

  // When rounding to 0 decimals with Sub-1% Style set to "<1%", hide slices
  // under 1% rather than showing them as "0%". The "0%" style shows every slice.
  const filteredChartData = useMemo(
    () =>
      filterSubOnePercentSlices(
        chartData,
        config.percentageFormatting,
        config.subOnePercentStyle,
      ),
    [chartData, config.percentageFormatting, config.subOnePercentStyle],
  );

  const emphasisData = useMemo(() => {
    if (providedEmphasisData !== undefined) {
      return providedEmphasisData;
    }

    if (config.emphasis === "none") {
      return null;
    }

    let selectedSlice;
    if (config.emphasis === "highest") {
      let maxValue = -1;
      let maxIndex = Infinity;
      selectedSlice = null;

      filteredChartData.forEach((slice, index) => {
        // For ties, we need to check the original dataset order, not the current sorted order
        let originalIndex = index;

        // If this is a group slice, find the lowest original index among its members
        if (slice.isGroup && slice.groupId) {
          const group = config.groups?.find((g) => g.id === slice.groupId);
          if (group) {
            originalIndex = Math.min(
              ...group.sliceNames.map((sliceName) => {
                const originalSlice = config.series?.[0]?.data?.findIndex(
                  (s) => s.name === sliceName,
                );
                return originalSlice !== -1 ? originalSlice || 0 : Infinity;
              }),
            );
          }
        } else {
          // For individual slices, find their original index in the dataset
          originalIndex =
            config.series?.[0]?.data?.findIndex((s) => s.name === slice.name) ||
            0;
          if (originalIndex === -1) originalIndex = Infinity;
        }

        // Select if higher value, or same value but lower original index
        if (
          slice.y > maxValue ||
          (slice.y === maxValue && originalIndex < maxIndex)
        ) {
          maxValue = slice.y;
          maxIndex = originalIndex;
          selectedSlice = slice;
        }
      });
    } else if (config.emphasis?.startsWith("slice:")) {
      // Individual slice selection - extract slice name from "slice:SliceName" format
      const sliceName = config.emphasis.replace("slice:", "");
      selectedSlice = filteredChartData.find(
        (slice) => slice.name === sliceName,
      );
    } else if (config.emphasis?.startsWith("group:")) {
      // Group slice selection - extract group ID from "group:GroupId" format
      const groupId = config.emphasis.replace("group:", "");
      const group = config.groups?.find((g) => g.id === groupId);

      if (group) {
        if (group.showAsSlice) {
          // Find the group slice in chart data
          selectedSlice = chartData.find((slice) => slice.groupId === groupId);
        } else {
          // Calculate group total from individual slices
          const groupTotal = group.sliceNames.reduce((sum, sliceName) => {
            const slice = config.series?.[0]?.data?.find(
              (s) => s.name === sliceName,
            );
            return sum + (slice?.y || 0);
          }, 0);

          selectedSlice = {
            name: group.name,
            y: groupTotal,
            color: "#666666", // Neutral color for emphasis
          };
        }
      }
    } else {
      // Legacy support or unknown emphasis type
      selectedSlice = null;
    }

    if (!selectedSlice) return null;

    // Calculate the total value for percentage calculation
    const total = filteredChartData.reduce((sum, slice) => sum + slice.y, 0);
    const percentageValue = total > 0 ? (selectedSlice.y / total) * 100 : 0;

    const percentage = formatPercentage(percentageValue);
    const displayText =
      config.emphasisDisplayFormat === "percentage_custom"
        ? config.emphasisCustomText
        : resolveLabel(selectedSlice.name);

    return {
      percentage: `${percentage}%`,
      text: displayText || "",
    };
  }, [
    providedEmphasisData,
    filteredChartData,
    config.emphasis,
    config.emphasisDisplayFormat,
    config.emphasisCustomText,
    config.percentageFormatting,
    config.groups,
    config.series,
    config.sliceCustomization,
  ]);

  const options = useMemo(() => {
    const {
      title,
      subtitle,
      innerRadius,
      outerRadius,
      startAngle,
      dataLabelsEnabled,
      dataLabelsDistance,
      legendEnabled,
      legendPosition,
      // Don't extract legendLayout - we'll calculate it dynamically
      allowPointSelect,
      sliceOffset,
      centerText,
      centerTextEnabled,
      showTooltip,
      series,
    } = config;

    return {
      chart: {
        type: "pie",
        backgroundColor: "transparent",
        events: {
          render() {
            const chart = this as any;
            const chartSeries = chart.series[0];

            // Only render center text for donut charts (innerRadius > 0)
            const currentInnerRadius = isConfigPreview
              ? innerRadius || 0
              : config?.innerRadius || 60;
            if (currentInnerRadius > 0 && chartSeries.center) {
              let customLabel = chart.options.chart.custom?.label;

              // If emphasis is "none" and no centerText, remove the label
              if (config.emphasis === "none" && !centerTextEnabled) {
                if (customLabel) {
                  customLabel.destroy();
                  chart.options.chart.custom.label = null;
                }
                return;
              }

              // Calculate responsive sizes based on inner circle
              const innerDiameter =
                chartSeries.center[2] * (currentInnerRadius / 100);
              const maxWidth = Math.max(innerDiameter * 0.75, 50);

              // Hoisted so the positioning step below (shared by both the
              // create and update branches) can read the final label text.
              let labelText = "";

              if (!customLabel) {
                // Create the label only once

                // Priority 1: Emphasis data (if available and not "none")
                if (emphasisData && config.emphasis !== "none") {
                  const emphasisTextHtml = escapeHtml(emphasisData.text);
                  labelText = `<div title="${emphasisTextHtml}" style="text-align: center; line-height: 1.2; width: ${maxWidth}px; max-height: ${innerDiameter * 0.6}px; overflow: hidden;">
                    <div style="font-size: 75%; word-break: break-word; white-space: normal; color: #000; margin: 0 auto 0.3em; text-transform: capitalize; display: -webkit-box; -webkit-line-clamp: 4; -webkit-box-orient: vertical; overflow: hidden; text-overflow: ellipsis;">${emphasisTextHtml}</div>
                    <div style="font-size: 100%; font-weight: bold; color: #000; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${emphasisData.percentage}</div>
                  </div>`;
                }
                // Priority 2: Fallback center text (only if no emphasis and centerTextEnabled)
                else if (
                  centerTextEnabled &&
                  centerText &&
                  (isConfigPreview ? config.emphasis === "none" : !emphasisData)
                ) {
                  labelText = `<div  style="text-align: center; word-break: break-word; white-space: normal; width: ${maxWidth}px; max-height: ${innerDiameter * 0.6}px; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 4; -webkit-box-orient: vertical; text-overflow: ellipsis;">${escapeHtml(centerText)}</div>`;
                }

                if (labelText) {
                  // Initialize custom object if it doesn't exist
                  if (!chart.options.chart.custom) {
                    chart.options.chart.custom = {};
                  }

                  customLabel = chart.options.chart.custom.label =
                    chart.renderer
                      .label(labelText, 0, 0, undefined, null, null, true)
                      .css({
                        color: "#666666",
                        textAnchor: "middle",
                        textAlign: "center",
                      })
                      .attr({
                        useHTML: true,
                      })
                      .add();
                }
              } else {
                // Update existing label content if needed
                if (emphasisData && config.emphasis !== "none") {
                  const emphasisTextHtml = escapeHtml(emphasisData.text);
                  labelText = `<div title="${emphasisTextHtml}" style="text-align: center; line-height: 1.2; width: ${maxWidth}px; max-height: ${innerDiameter * 0.6}px; overflow: hidden;">
                    <div style="font-size: 75%; word-break: break-word; white-space: normal; color: #000; margin: 0 auto 0.3em; text-transform: capitalize; display: -webkit-box; -webkit-line-clamp: 4; -webkit-box-orient: vertical; overflow: hidden; text-overflow: ellipsis;">${emphasisTextHtml}</div>
                    <div style="font-size: 100%; font-weight: bold; color: #000; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${emphasisData.percentage}</div>
                  </div>`;
                } else if (
                  centerTextEnabled &&
                  centerText &&
                  config.emphasis === "none"
                ) {
                  labelText = `<div style="text-align: center; word-break: break-word; white-space: normal; width: ${maxWidth}px; max-height: ${innerDiameter * 0.6}px; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 4; -webkit-box-orient: vertical; text-overflow: ellipsis;">${escapeHtml(centerText)}</div>`;
                }

                if (labelText && customLabel.attr("text") !== labelText) {
                  customLabel.attr("text", labelText);
                } else if (!labelText) {
                  // If no label text, destroy the label
                  customLabel.destroy();
                  chart.options.chart.custom.label = null;
                  return;
                }
              }

              // Position the label at the center. `render` fires on every chart
              // redraw, including ones triggered by point hover/tooltip — skip
              // the font-size + bbox-measuring reflow when nothing that affects
              // the label's size or position actually changed, so hovering
              // slices doesn't fight the tooltip animation with redundant work.
              if (customLabel) {
                const textLength =
                  emphasisData?.text?.length || centerText?.length || 0;
                const positionKey = [
                  labelText,
                  chartSeries.center.join(","),
                  textLength,
                ].join("|");

                if (chart.options.chart.custom.lastPositionKey !== positionKey) {
                  chart.options.chart.custom.lastPositionKey = positionKey;

                  // Calculate font size based on text length and chart size
                  let baseFontSize = Math.max(12, chartSeries.center[2] / 12);

                  // Reduce font size for longer text, minimum 14px
                  if (textLength > 50) {
                    baseFontSize = Math.max(14, baseFontSize * 0.7);
                  } else if (textLength > 30) {
                    baseFontSize = Math.max(14, baseFontSize * 0.85);
                  }

                  // Set font size first
                  customLabel.css({
                    fontSize: `${baseFontSize}px`,
                  });

                  // Use setTimeout to ensure dimensions are calculated after font size is applied
                  setTimeout(() => {
                    if (customLabel && chartSeries.center) {
                      const labelWidth = customLabel.getBBox
                        ? customLabel.getBBox().width
                        : customLabel.attr("width");
                      const labelHeight = customLabel.getBBox
                        ? customLabel.getBBox().height
                        : customLabel.attr("height");

                      const x =
                        chartSeries.center[0] + chart.plotLeft - labelWidth / 2;
                      const y =
                        chartSeries.center[1] + chart.plotTop - labelHeight / 2;

                      customLabel.attr({
                        x,
                        y,
                      });
                    }
                  }, 0);
                }
              }
            }
          },
        },
      },
      title: {
        text: undefined, // Title is displayed in widget header, not in chart
        style: {
          fontSize: "18px",
          fontWeight: config.titleBold ? "700" : "500",
          textDecoration: config.titleUnderline ? "underline" : "none",
        },
      },
      subtitle: {
        text: undefined, // Subtitle is displayed in widget header, not in chart
        style: {
          fontSize: "14px",
        },
      },
      tooltip: {
        enabled: showTooltip !== false,
        useHTML: true,
        // `outside: true` (tooltip rendered in its own container at the
        // document body level) was tried here to fix z-order against the
        // center-emphasis label, but it makes Highcharts create/resize a
        // second SVG renderer on every position update — with
        // followPointer's raw per-pixel updates that reads as laggy. Leave
        // it off; the mouse can only be hovering a rendered slice pixel to
        // begin with, so the tooltip anchor never lands over the center
        // label in practice.
        style: { zIndex: 9999 },
        // Follow the raw mouse pixel within the slice (Highcharts' default
        // for pie/donut series) rather than anchoring to a fixed point —
        // moving within a slice moves the tooltip with it, no fixed spot.
        followPointer: true,
        formatter: function (this: any) {
          const decimals =
            config.percentageFormatting !== undefined
              ? config.percentageFormatting
              : 1;
          const percentage = this.point.percentage.toFixed(decimals);
          const label = escapeHtml(resolveLabel(this.point.name));
          const base =
            config.sliceValueFormat === "percentage_text"
              ? `<b>${percentage}% ${label}</b>`
              : `${label}: <b>${percentage}%</b>`;
          const cellN = this.point.cellN;
          if (config.showSliceN && cellN != null) {
            return `${base}<br/>N: ${cellN.toLocaleString()}`;
          }
          return base;
        },
      },
      accessibility: {
        point: {
          valueSuffix: "%",
          // Always provide a formatter (not just when Show Slice N is on) so
          // the accessible name reflects Slice Customization overrides too.
          descriptionFormatter: function (point: any) {
            const decimals =
              config.percentageFormatting !== undefined
                ? config.percentageFormatting
                : 1;
            const pct = point.y != null ? `${point.y.toFixed(decimals)}%` : "";
            const nSuffix =
              config.showSliceN && point.cellN != null
                ? `, N = ${point.cellN.toLocaleString()}`
                : "";
            return `${resolveLabel(point.name)}, ${pct}${nSuffix}`;
          },
        },
      },
      legend: {
        enabled: false, // Disabled - using custom sidebar instead
      },
      plotOptions: {
        pie: {
          innerSize: `${isConfigPreview ? innerRadius : config?.innerRadius || 60}%`,
          size: `${isConfigPreview ? outerRadius : config?.outerRadius || 80}%`,
          startAngle: startAngle || 0,
          allowPointSelect: allowPointSelect !== false,
          cursor: allowPointSelect !== false ? "pointer" : "default",
          showInLegend: legendEnabled !== false,
          slicedOffset: sliceOffset || 10,
          center: ["50%", "50%"], // Always center the pie chart
          dataLabels: {
            enabled: dataLabelsEnabled !== false,
            useHTML: true, // Enable HTML rendering for arrows
            formatter: function (this: any) {
              const point = this.point;
              const percentage = formatPercentage(point.percentage);

              // Apply minimum threshold logic (unless forceShowAllValues is enabled)
              const minThreshold = config.minSliceTextThreshold || 2;
              const forceShowAll = config.forceShowAllValues || false;

              // Hide data labels for slices below minimum threshold
              // unless forceShowAllValues is enabled
              if (!forceShowAll && point.percentage < minThreshold) {
                return null; // This hides the data label but keeps legend entry
              }

              // Get significance arrow if available
              const arrow = getSignificanceArrow(point.significantDifference);

              if (config.sliceValueFormat === "percentage_text") {
                const label = resolveLabel(point.name);
                // Bar Chart's category-text truncation rule (40 chars), applied
                // only to outside labels — tooltips and accessible text always
                // show the full name.
                const shouldTruncate =
                  config.truncateCategoryText &&
                  config.dataLabelsPosition === "outside";
                const displayName =
                  shouldTruncate && label.length > 40
                    ? label.substring(0, 40) + "..."
                    : label;

                return `${percentage}%${arrow} ${escapeHtml(displayName)}`;
              } else {
                return `${percentage}%${arrow}`;
              }
            },
            distance: dataLabelsDistance || 15,
            style: {
              fontSize: "11px",
              textOutline: "none",
            },
          },
        },
        series: {
          animation: {
            duration: 300,
          },
        },
      },
      credits: {
        enabled: false,
      },

      exporting: {
        enabled: false,
      },
      series: [
        {
          type: "pie",
          name: series?.[0]?.name || (isConfigPreview ? "Data" : "Share"),
          data: filteredChartData,
        } as any,
      ],
      // Optional responsive tweak
      responsive: {
        rules: [
          {
            condition: { maxWidth: 480 },
            chartOptions: {
              plotOptions: { pie: { dataLabels: { distance: 8 } } },
              legend: { enabled: false },
            },
          },
        ],
      },
    };
  }, [
    config,
    filteredChartData,
    emphasisData,
    isConfigPreview,
    config.percentageFormatting,
    config.legendPosition,
    config.dataLabelsDistance,
  ]);

  // Exposed so callers build the legend from the same slice set actually
  // rendered on the pie — otherwise slices hidden by the sub-1% filter above
  // still show up as legend rows with nothing to click on.
  return { options, filteredChartData };
};
